import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../../../store';
import type {
  ExternalConnectionTO,
  MyPainHealthUserStatus,
  PatientProfileTO,
} from '../../../../lib';
import { usePatientProfileSettings } from './usePatientProfileSettings';
import { postRequestSync, updatePatientProfile } from '../../../../store/ai2';
import { useForm, type UseFormReturn } from 'react-hook-form';

type PatientProfileState = {
  openClinics: boolean;
  setOpenClinics: (value: boolean) => void;
  openPatientInfo: boolean;
  setOpenPatientInfo: (value: boolean) => void;
  editProfile: boolean;
  setEditProfile: (value: boolean) => void;
  formSubmitted: boolean;
  setFormSubmitted: (value: boolean) => void;
  dropdownOpen: boolean;
  setDropdownOpen: (value: boolean) => void;
  clinicMeta: any[];
  setClinicMeta: (value: any[]) => void;
  onToggleClinics: () => void;
  handleClinicMetaChange: (updatedMeta: any) => void;
  openReconciliationSummary: boolean;
  setOpenReconciliationSummary: (value: boolean) => void;
  openAdditionalInsights: boolean;
  setOpenAdditionalInsights: (value: boolean) => void;
  openMedicationProfile: boolean;
  onOpenMedicationProfile: () => void;
  onCloseMedicationProfile: () => void;
  toggleMedicationProfile: () => void;
  patientProfile: PatientProfileTO;
  actionBarGroup: any;
  themeStyle: any;
  onSubmit: (formData: any) => void;
  form: UseFormReturn<Record<string, any>, any>;
  handleSyncClick: (accessToken: string) => void;
  isSynced: boolean;
  patientMedProfileActive: boolean;
  patientOSANavigatorActive: boolean;
  patientMyPainHealthUserStatus: MyPainHealthUserStatus;
  setPatientMyPainHealthUserStatus: (status: MyPainHealthUserStatus) => void;
  externalConnections?: ExternalConnectionTO[];
};

const PatientProfileContext = createContext<PatientProfileState | undefined>(
  undefined,
);

export const PatientProfileProvider = ({
  children,
  dataAnnotation = false,
}: {
  children: React.ReactNode;
  dataAnnotation?: boolean;
}) => {
  const { actionBarGroup, themeStyle } = usePatientProfileSettings();
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const form = useForm<Record<string, any>>({});
  const dispatch = useDispatch();
  const [openClinics, setOpenClinics] = useState(false);
  const [openPatientInfo, setOpenPatientInfo] = useState(false);
  const [editProfile, setEditProfile] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [clinicMeta, setClinicMeta] = useState<any[]>([]);
  const [openReconciliationSummary, setOpenReconciliationSummary] =
    useState(false);
  const [openAdditionalInsights, setOpenAdditionalInsights] = useState(false);
  const [openMedicationProfile, setMedicationProfile] = useState(false);
  const [isSynced, setIsSynced] = useState(false);
  const handleSyncClick = (accessToken: string) => {
    setIsSynced(!isSynced);
    postRequestSync(accessToken, selectedTenancy);
  };
  const onToggleClinics = () => setOpenClinics((prev) => !prev);
  const handleClinicMetaChange = (updatedMeta: any) =>
    setClinicMeta(updatedMeta);

  const onOpenMedicationProfile = () => setMedicationProfile(true);
  const onCloseMedicationProfile = () => setMedicationProfile(false);
  const toggleMedicationProfile = () => setMedicationProfile((prev) => !prev);

  const {
    patientProfile: normalPatientProfile,
    medicineProfileAccountActive,
    externalConnections,
  } = useSelector((state: RootState) => state.patientProfile);

  const { dataAnnotationPatientProfile } = useSelector(
    (state: RootState) => state.dataAnnotation,
  );
  const patientProfile = React.useMemo(
    () =>
      dataAnnotation && dataAnnotationPatientProfile
        ? (dataAnnotationPatientProfile as unknown as PatientProfileTO)
        : normalPatientProfile,
    [dataAnnotation, dataAnnotationPatientProfile, normalPatientProfile],
  );

  const patientMedProfileActive = Boolean(
    medicineProfileAccountActive ||
      normalPatientProfile.medicineProfileAccountActive ||
      dataAnnotationPatientProfile?.medicineProfileAccountActive,
  );

  const patientOSANavigatorActive = Boolean(
    normalPatientProfile.osaNavigatorAccountActive ||
      dataAnnotationPatientProfile?.osaNavigatorAccountActive,
  );

  const getMyPainHealthUserStatus = (
    profile: PatientProfileTO,
  ): MyPainHealthUserStatus => ({
    created: profile?.patientMyPainHealthUserStatus?.created || false,
    onboarded: profile?.patientMyPainHealthUserStatus?.onboarded || false,
    active: profile?.patientMyPainHealthUserStatus?.active || false,
  });

  const [patientMyPainHealthUserStatus, setPatientMyPainHealthUserStatus] =
    useState<MyPainHealthUserStatus>(() =>
      getMyPainHealthUserStatus(normalPatientProfile),
    );

  useEffect(() => {
    if (!patientProfile?.patientMyPainHealthUserStatus) {
      return;
    }
    setPatientMyPainHealthUserStatus(getMyPainHealthUserStatus(patientProfile));
  }, [
    patientProfile?.patientMyPainHealthUserStatus?.created,
    patientProfile?.patientMyPainHealthUserStatus?.onboarded,
    patientProfile?.patientMyPainHealthUserStatus?.active,
  ]);

  const onSubmit = (formData: any) => {
    setFormSubmitted(true);
    dispatch(updatePatientProfile(formData) as any);
    setEditProfile(false);
  };
  return (
    <PatientProfileContext.Provider
      value={{
        actionBarGroup,
        themeStyle,
        openClinics,
        setOpenClinics,
        openPatientInfo,
        setOpenPatientInfo,
        editProfile,
        setEditProfile,
        formSubmitted,
        setFormSubmitted,
        dropdownOpen,
        setDropdownOpen,
        clinicMeta,
        setClinicMeta,
        onToggleClinics,
        handleClinicMetaChange,
        openReconciliationSummary,
        setOpenReconciliationSummary,
        openAdditionalInsights,
        setOpenAdditionalInsights,
        openMedicationProfile,
        onOpenMedicationProfile,
        onCloseMedicationProfile,
        toggleMedicationProfile,
        patientProfile,
        onSubmit,
        form,
        handleSyncClick,
        isSynced,
        patientMedProfileActive,
        patientOSANavigatorActive,
        externalConnections,
        patientMyPainHealthUserStatus,
        setPatientMyPainHealthUserStatus,
      }}
    >
      {children}
    </PatientProfileContext.Provider>
  );
};

export const usePatientProfile = (): PatientProfileState => {
  const context = useContext(PatientProfileContext);
  if (!context) {
    throw new Error(
      'usePatientProfile must be used within a PatientProfileProvider',
    );
  }
  return context;
};
